import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import accountApi from '~/apis/account';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch } from '~/redux/hooks';
import { commonActions } from '~/thunks/common/commonSlice';
import { ROLE_DISTRIBUTOR } from '~/utils/constants/account';
import { handleApiError, notify } from '~/utils/helper';
import { IAccount, IUpdatePassword } from '~/utils/interface/account';
import distributorValidation from '~/validation/distributor';

type SendPasswordDialogProps = {
  open: boolean;
  data?: IAccount;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IUpdatePassword = {
  username: '',
  new_password: '',
};
const SendPasswordDialog = (props: SendPasswordDialogProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/distributor');

  const form = useFormik({
    validateOnChange: false,
    validationSchema: distributorValidation(t).updatePassword,
    initialValues,
    onSubmit: async (values) => {
      dispatch(commonActions.setIsLoading(true));
      try {
        await accountApi.updatePassword(
          { role: ROLE_DISTRIBUTOR },
          { ...values, username: String(props.data?.username) }
        );
        notify('success', t('sendPasswordDialog.sendEmailSuccess'));
        handleSetOpen(false, false);
      } catch (e: any) {
        handleApiError(e);
      } finally {
        dispatch(commonActions.setIsLoading(false));
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle className='bg-[#ED1002]'>
          {t('sendPasswordDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography className='bg-[#005F73] p-3 rounded-[4px] text-white font-bold text-[15px] mb-4'>
            {t('sendPasswordDialog.notify')}
          </Typography>
          <Typography className='text-[#005F73] mb-2.5 font-bold'>{t('sendPasswordDialog.info')}</Typography>
          <Box className='flex mb-2.5 gap-2.5'>
            <Box className='font-bold'>{t('sendPasswordDialog.name')}: </Box>
            <Box className='font-normal'>{props.data?.name}</Box>
          </Box>
          <Box className='flex mb-2.5 gap-2.5'>
            <Box className='font-bold'>{t('sendPasswordDialog.email')}: </Box>
            <Box className='font-normal'>{props.data?.email}</Box>
          </Box>
          <Box className='flex mb-2.5 gap-2.5'>
            <Box className='font-bold'>{t('sendPasswordDialog.username')}: </Box>
            <Box className='font-normal'>{props.data?.username}</Box>
          </Box>
          <Typography className='text-[#005F73] mt-5 mb-2.5 font-bold'>
            {t('sendPasswordDialog.accountInfo')}
          </Typography>
          <Box className='flex mb-2.5'>
            <Box className='font-bold'>{t('sendPasswordDialog.loginUsername')}:</Box>
            <Box className='font-bold ml-auto'>{props.data?.username}</Box>
          </Box>
          <BaseRow className='gap-5 mb-5 !items-center !justify-between'>
            <Box className='w-1/2 font-bold flex items-center'>
              <InputLabel>{t('sendPasswordDialog.newPassword')}:</InputLabel>
            </Box>
            <TextField
              size='medium'
              className='w-1/2'
              name='new_password'
              placeholder={t('enter', { ns: 'admin/common' })}
              value={form.values?.new_password}
              onChange={form.handleChange}
              error={Boolean(form.errors?.new_password)}
              helperText={form.errors?.new_password}
            />
          </BaseRow>
          <Typography className='text-[#005F73] font-bold'>{t('sendPasswordDialog.preview')}</Typography>
          <Box className='flex my-2'>
            <Box className='font-bold'>{t('sendPasswordDialog.to')}:</Box>
            <Box className='font-normal ml-2.5'>{props.data?.email}</Box>
          </Box>
          <Box
            className='border border-[#005F73] rounded-[8px] p-[12px] leading-[120%] mb-[23px]'
            dangerouslySetInnerHTML={{
              __html: t('sendPasswordDialog.emailContent', {
                name: props.data?.name,
                username: props.data?.username,
                new_password: form.values.new_password,
              }),
            }}
          ></Box>
          <Box>
            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto'
              onClick={() => form.handleSubmit()}
            >
              {t('sendPasswordDialog.sendEmail')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(SendPasswordDialog);
